import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { logUtishaDebug, logUtishaInfo, logUtishaWarn } from 'src/app/util/log';

export type CpuPressureLevel = 'normal' | 'reduced' | 'minimal';

/**
 * Maps Compute Pressure API states to our internal levels.
 * See https://developer.mozilla.org/en-US/docs/Web/API/Compute_Pressure_API
 */
const PRESSURE_API_MAP: Record<string, CpuPressureLevel> = {
  'nominal': 'normal',
  'fair': 'normal',
  'serious': 'reduced',
  'critical': 'minimal',
};

/**
 * Centralized CPU pressure monitor with two strategies:
 *
 * 1. **PressureObserver** (Chrome 125+, Edge) — reads actual OS-level CPU pressure.
 *    No heuristics, no false positives from background tabs or network issues.
 *
 * 2. **Frame-rate fallback** (Safari, Firefox, older browsers) — infers pressure
 *    from rendering frame-rate deviation. Guarded against background-tab throttling
 *    and tuned for mobile devices that cap at 30 FPS.
 *
 * Pressure levels:
 * - normal:  full quality
 * - reduced: throttle non-critical work (canvas 540p, audio monitor 200ms)
 * - minimal: shed all optional load (canvas 360p, audio monitor paused)
 */
export class PerformanceMonitor {
  private _pressure$ = new BehaviorSubject<CpuPressureLevel>('normal');
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private cpuScoreProvider: (() => number) | null = null;

  // Hysteresis for frame-rate fallback
  private consecutiveCount = 0;
  private pendingLevel: CpuPressureLevel = 'normal';
  private readonly HYSTERESIS_THRESHOLD = 3; // 3 checks × 2s = 6s sustained before transition
  private readonly CHECK_INTERVAL_MS = 2000;

  // PressureObserver (Chrome 125+, Edge)
  private pressureObserver: any = null;
  private usingNativeAPI = false;

  // Visibility tracking — skip frame-rate evaluation when tab is backgrounded
  private visibilityHandler: (() => void) | null = null;
  private tabVisible = true;

  readonly pressure$: Observable<CpuPressureLevel> = this._pressure$.pipe(distinctUntilChanged());

  get pressure(): CpuPressureLevel {
    return this._pressure$.value;
  }

  /**
   * Register a function that returns the current CPU usage score (0.0 = overloaded, 1.0 = idle).
   * Only used in frame-rate fallback mode. Ignored when PressureObserver is available.
   */
  setCpuScoreProvider(provider: () => number): void {
    this.cpuScoreProvider = provider;
  }

  start(): void {
    if (this.checkInterval || this.pressureObserver) return;

    // Strategy 1: Try native Compute Pressure API
    if (this.tryStartPressureObserver()) {
      this.usingNativeAPI = true;
      logUtishaDebug('PerformanceMonitor: started (PressureObserver — native CPU pressure)');
      return;
    }

    // Strategy 2: Frame-rate fallback with visibility guard
    this.startVisibilityTracking();
    this.checkInterval = setInterval(() => this.evaluateFrameRate(), this.CHECK_INTERVAL_MS);
    logUtishaDebug('PerformanceMonitor: started (frame-rate fallback)');
  }

  stop(): void {
    if (this.pressureObserver) {
      try { this.pressureObserver.disconnect(); } catch (_) {}
      this.pressureObserver = null;
    }
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.stopVisibilityTracking();
    this._pressure$.next('normal');
    this.consecutiveCount = 0;
    this.usingNativeAPI = false;
    logUtishaDebug('PerformanceMonitor: stopped');
  }

  /**
   * Attempt to use the Compute Pressure API (PressureObserver).
   * Available in Chrome 125+, Edge 125+.
   * Returns true if successfully started, false if not available.
   */
  private tryStartPressureObserver(): boolean {
    if (typeof (globalThis as any).PressureObserver === 'undefined') {
      return false;
    }

    try {
      const PressureObserver = (globalThis as any).PressureObserver;

      this.pressureObserver = new PressureObserver(
        (records: any[]) => {
          if (records.length === 0) return;
          const latest = records[records.length - 1];
          const state: string = latest.state; // 'nominal' | 'fair' | 'serious' | 'critical'
          const level = PRESSURE_API_MAP[state] ?? 'normal';
          this.applyPressureLevel(level, `native=${state}`);
        },
        { sampleInterval: this.CHECK_INTERVAL_MS }
      );

      this.pressureObserver.observe('cpu');
      return true;
    } catch (e) {
      logUtishaDebug(`PerformanceMonitor: PressureObserver not usable: ${e}`);
      return false;
    }
  }

  /**
   * Frame-rate fallback evaluation.
   * Skips evaluation when the tab is hidden (browser throttles rAF to ~1 FPS).
   */
  private evaluateFrameRate(): void {
    // Don't evaluate when tab is backgrounded — rAF is throttled by the browser,
    // not by CPU pressure. This prevents false positives on all platforms.
    if (!this.tabVisible) {
      return;
    }

    const score = this.cpuScoreProvider ? this.cpuScoreProvider() : 1.0;
    let level: CpuPressureLevel;

    if (score >= 0.6) {
      level = 'normal';
    } else if (score >= 0.35) {
      level = 'reduced';
    } else {
      level = 'minimal';
    }

    // Hysteresis: require sustained level change before transitioning
    if (level === this.pendingLevel) {
      this.consecutiveCount++;
    } else {
      this.pendingLevel = level;
      this.consecutiveCount = 1;
    }

    this.applyPressureLevel(level, `score=${score.toFixed(2)}`);
  }

  /**
   * Shared transition logic — immediate degradation, hysteresis-gated recovery.
   */
  private applyPressureLevel(level: CpuPressureLevel, debugContext: string): void {
    const currentPressure = this._pressure$.value;
    const pressureOrder: CpuPressureLevel[] = ['normal', 'reduced', 'minimal'];
    const currentIndex = pressureOrder.indexOf(currentPressure);
    const newIndex = pressureOrder.indexOf(level);

    if (newIndex > currentIndex) {
      // Degrading — apply immediately for responsiveness
      this._pressure$.next(level);
      this.consecutiveCount = 0;
      logUtishaWarn(`PerformanceMonitor: CPU pressure escalated to '${level}' (${debugContext})`);
    } else if (newIndex < currentIndex) {
      if (this.usingNativeAPI || this.consecutiveCount >= this.HYSTERESIS_THRESHOLD) {
        // Native API: trust the OS signal, apply recovery immediately.
        // Frame-rate fallback: require sustained improvement.
        this._pressure$.next(level);
        this.consecutiveCount = 0;
        logUtishaInfo(`PerformanceMonitor: CPU pressure recovered to '${level}' (${debugContext})`);
      }
    }
  }

  private startVisibilityTracking(): void {
    this.tabVisible = document.visibilityState === 'visible';
    this.visibilityHandler = () => {
      const wasHidden = !this.tabVisible;
      this.tabVisible = document.visibilityState === 'visible';

      if (wasHidden && this.tabVisible) {
        // Tab just became visible — reset hysteresis to avoid stale frame-rate data
        // triggering a false degradation from the backgrounded period.
        this.consecutiveCount = 0;
        this.pendingLevel = this._pressure$.value;
        logUtishaDebug('PerformanceMonitor: tab visible — reset frame-rate hysteresis');
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private stopVisibilityTracking(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  destroy(): void {
    this.stop();
    this._pressure$.complete();
  }
}
